import { useEffect, useMemo, useRef } from 'react';

import { t } from 'i18next';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputMask, InputMaskChangeEvent } from 'primereact/inputmask';
import { InputNumber, InputNumberChangeEvent } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Panel, PanelToggleEvent } from 'primereact/panel';
import { SelectButton } from 'primereact/selectbutton';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import {
  useLazyGetNextScheduleQuery,
  useLazyGetScheduledDrugsQuery,
} from '@/app/services/donors';
import {
  useLazySearchDrugsQuery,
  useSearchSystemUsersQuery,
} from '@/app/services/lookups';
import { DonorSchedulingActions } from '@/app/slices/donorScheduling';
import { PartnershipsActions } from '@/app/slices/partnerships';
import { addToast } from '@/app/slices/toast';
import {
  AccountAutoComplete,
  CsrAutoComplete,
  FertilizationSiteDropdown,
  SitesDropdown,
  StringMapDropdown,
} from '@/components';
import { AdvancedAccountLookup } from '@/components/common/advanced-account-lookup';
import { AdvancedDonorLookup } from '@/components/common/advanced-donor-lookup';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { Selection } from '@/components/common/sites-drop-down';
import { StringMapObjectTypeCode } from '@/components/common/string-map-dropdown';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { accountStatusOptions, SELECTION_LIMIT } from '@/constants';
import {
  LOAD_DONOR_PARTNERS_OPTION_VALUES,
  PREGNANT_OPTION_VALUES,
} from '@/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFormNav } from '@/hooks/useFormNav';
import { Donor, DonorSchedule } from '@/rtk-query.gql';
import { queryClient } from '@/services';
import { getValidSites } from '@/services/auth';
import { useUpsertDonorSchedules } from '@/services/donor-scheduling/hooks';
import { partnershipsOptions } from '@/services/partnerships';

import {
  BILL_ACCOUNT_CANING_OPTIONS,
  useAllowedNullFields,
} from '../../../constants';
import {
  findExactDrugByName,
  getFolltropinDrugName,
  isNoStimSchedule,
  resolveFshDrug1,
  resolveFshDrug2,
} from '../../../util/fshDefaulting';
import { removeNulls } from '../../../util/util';
import { PartnershipForm } from '../../partnership-form';
import {
  PartnershipTypeLabel,
  PartnershipTypeValue,
} from '../../partnership-form/constants';
import {
  BILL_PARTNERSHIP_SELECT_OPTIONS,
  TRUE_FALSE_SELECT_OPTIONS,
  ZERO_ONE_SELECT_OPTIONS,
} from '../constants';

type DonorScheduleFormGeneralProps = {
  className?: string;
  dialogContainer?: HTMLElement;
  collapsed: boolean;
  viewOnly?: boolean;
  onCollapseChange: (collapsed: boolean) => void;
  revisionComment: string;
  setRevisionComment: (revisionComment: string) => void;
  revisionNumber: {
    previousRevision: string;
    currentRevision: string;
  };
  setRevisionNumber: (revisionNumber: {
    previousRevision: string;
    currentRevision: string;
  }) => void;

  unlockAll: boolean | null;
  setUnlockAll: (unlocked: boolean) => void;
  handleSoScheduleDialogYes?: () => void;
};

export const DonorScheduleFormGeneral = ({
  className = '',
  dialogContainer,
  collapsed,
  viewOnly = false,
  onCollapseChange,
  revisionComment,
  setRevisionComment,
  revisionNumber,
  setRevisionNumber,
  unlockAll,
  setUnlockAll,
  handleSoScheduleDialogYes,
}: DonorScheduleFormGeneralProps) => {
  const {
    ref: formNavRef,
    onKeyDownCapture,
    selectOnFocus,
    handleEnterKeyAdvance,
  } = useFormNav<HTMLDivElement>({
    selectors: [
      'input:not([type="hidden"]):not([disabled]):not([readonly])',
      'select:not([disabled])',
      '.p-dropdown:not(.p-disabled) input',
      '.p-dropdown:not(.p-disabled) input[role="combobox"]',
      '.p-dropdown:not(.p-disabled) .p-dropdown-label',
      '.p-autocomplete:not(.p-disabled) input',
      '.p-calendar:not(.p-disabled) input',
      '.p-inputmask:not(.p-disabled) input',
      '.p-inputnumber:not(.p-disabled) input',
      '.p-selectbutton:not(.p-disabled) .p-button:first-child',
      '.p-nucleus:not(.p-disabled) .p-button:first-child',
      'textarea:not([disabled]):not([readonly])',
      'button:not([disabled])[tabindex]',
    ],
    wrap: true,
    sortByTabIndex: true,
    skipAmount: 2,
    allowDropdownEnterSelect: true,
  });

  const dispatch = useDispatch();
  const { me } = useAppSelector((state) => state.authentication);

  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );
  const isDsDefaultSoEnabled = useFeatureFlag('DS_Default_SO');
  const isAdvancedAccountSearchEnabled = useFeatureFlag(
    'DS_Account_Advanced_Search',
  );
  const allowedNullFields = useAllowedNullFields();
  const isCreateSchedulesBeforeCurrentDateEnabled = useFeatureFlag(
    'Create_Schedules_Before_Current_Date',
  );
  const isRequiredFieldAsterisksEnabled = useFeatureFlag(
    'DS_Required_Field_Asterisks',
  );
  const donorChangeToastsRef = useRef<string | null>(null);
  const [params, setParams] = useSearchParams();
  const id = params.get('id');
  const [getNextSchedule] = useLazyGetNextScheduleQuery();
  const [getScheduledDrugs] = useLazyGetScheduledDrugsQuery();
  const [searchDrugs] = useLazySearchDrugsQuery();
  const fshDrugs = useAppSelector((state) => state.lookups.fshDrugs);

  const [upsertDonorSchedules] = useUpsertDonorSchedules();

  const { data } = useSearchSystemUsersQuery({
    search: me.email,
    selectionLimit: 1,
  });
  const hasSoSchedule = useMemo(() => {
    return workingDonorSchedule?.soSchedule?.id != null;
  }, [workingDonorSchedule?.soSchedule?.id]);

  useEffect(() => {
    if (
      !collapsed &&
      workingDonorSchedule?.donorId &&
      workingDonorSchedule?.id
    ) {
      const pollInterval = setInterval(async () => {
        try {
          const partnershipData = await queryClient.fetchQuery(
            partnershipsOptions({
              donorId: workingDonorSchedule.donorId,
              donorScheduleId: workingDonorSchedule.id,
            }),
          );
          const partnership = partnershipData?.[0];

          if (partnership) {
            dispatch({
              type: PartnershipsActions.UPDATE_PARTNERSHIP,
              payload: [
                {
                  ...partnership,
                  donor: workingDonorSchedule?.donor,
                  donorId: workingDonorSchedule?.donorId,
                },
              ],
            });
          }
        } catch (error) {
          console.error('Failed to poll partnerships:', error);
        }
      }, 30000);

      return () => clearInterval(pollInterval);
    }
  }, [
    collapsed,
    workingDonorSchedule?.donorId,
    workingDonorSchedule?.id,
    dispatch,
  ]);

  const closedNoNewBusinessValue =
    accountStatusOptions.find((opt) => opt.label === 'Closed - No New Business')
      ?.value ??
    accountStatusOptions.find((opt) => opt.value === 100000003)?.value ??
    null;

  const isPastDue = useMemo(() => {
    return !!(
      workingDonorSchedule?.owner?.aging180 ||
      workingDonorSchedule?.owner?.aging365 ||
      workingDonorSchedule?.owner?.accountStatus === closedNoNewBusinessValue
    );
  }, [
    workingDonorSchedule?.owner?.aging180,
    workingDonorSchedule?.owner?.aging365,
    workingDonorSchedule?.owner?.accountStatus,
  ]);

  const isFieldLocked = useMemo(() => {
    return hasSoSchedule && !unlockAll;
  }, [hasSoSchedule, unlockAll]);

  useEffect(() => {
    if (data && data.length > 0 && !workingDonorSchedule?.csr) {
      dispatch({
        type: DonorSchedulingActions.UPDATE_CSR,
        payload: {
          fullName: me?.displayName,
          id: data[0]?.id,
        },
      });
    }
  }, [data, workingDonorSchedule?.csr, dispatch, me?.displayName]);

  const csr = workingDonorSchedule?.csr;
  const isDateLessThanOrEqualToToday = useMemo(() => {
    return (
      new Date(workingDonorSchedule?.date) <=
      new Date(new Date().setHours(0, 0, 0, 0))
    );
  }, [workingDonorSchedule?.date]);

  const isCommercialAccount =
    workingDonorSchedule?.owner?.priceTier === 40 ||
    workingDonorSchedule?.owner?.priceTier === 41;
  const minDate = useMemo(() => {
    if (isCreateSchedulesBeforeCurrentDateEnabled) return undefined;
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [isCreateSchedulesBeforeCurrentDateEnabled]);
  const handleSoScheduleWarning = () => {
    if (isFieldLocked) {
      confirmDialog({
        message:
          'This donor has a SO Schedule. Changing the date or site will deactivate it. You will need to create a new SO Schedule. Do you want to proceed?',
        header: 'SO Schedule Warning',
        icon: 'pi pi-exclamation-triangle',
        accept: handleSoScheduleDialogYes,
        reject: handleSoScheduleDialogNo,
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        acceptClassName: 'p-button-warning',
        rejectClassName: 'p-button-secondary',
      });
    } else {
      if (setUnlockAll) {
        setUnlockAll(false);
      }
    }
  };

  useEffect(() => {
    if (
      isCommercialAccount &&
      workingDonorSchedule?.loadDonorPartners !==
        LOAD_DONOR_PARTNERS_OPTION_VALUES.NO
    ) {
      dispatch({
        type: DonorSchedulingActions.UPDATE_BILL_DONOR_PARTNERSHIP_STATUS,
        payload: LOAD_DONOR_PARTNERS_OPTION_VALUES.NO,
      });
    }
    if (
      workingDonorSchedule?.loadDonorPartners ===
        LOAD_DONOR_PARTNERS_OPTION_VALUES.YES &&
      workingDonorSchedule?.billAccountCaningOption !== null
    ) {
      dispatch({
        type: DonorSchedulingActions.UPDATE_BILL_ACCOUNT_CANING_OPTIONS,
        payload: null,
      });
    }
  }, [
    isCommercialAccount,
    workingDonorSchedule?.loadDonorPartners,
    dispatch,
    workingDonorSchedule?.billAccountCaningOption,
  ]);

  const handleDonorChange = async (donor: Donor) => {
    donorChangeToastsRef.current = donor.id;
    dispatch({
      type: DonorSchedulingActions.UPDATE_DONOR,
      payload: donor,
    });
    dispatch({
      type: DonorSchedulingActions.UPDATE_PROGRAM_NAME,
      payload: donor.programName ?? '',
    });

    const partnershipData = await queryClient.fetchQuery(
      partnershipsOptions({
        donorId: donor.id,
        donorScheduleId: workingDonorSchedule.id,
      }),
    );
    const partnership = partnershipData?.[0];

    if (partnership) {
      dispatch({
        type: PartnershipsActions.UPDATE_PARTNERSHIP,
        payload: [{ ...partnership, donor, donorId: donor.id }],
      });
      if (
        partnership.percent1 === 25 ||
        partnership.percent1 === 33 ||
        partnership.percent1 === 50
      ) {
        dispatch({
          type: DonorSchedulingActions.UPDATE_BILL_DONOR_PARTNERSHIP_STATUS,
          payload: LOAD_DONOR_PARTNERS_OPTION_VALUES.YES,
        });
      }
    }
    const nextSchedule = await getNextSchedule(donor.id).unwrap();
    const nextScheduleItems = nextSchedule.aspirations.items[0];
    const nextSchedulePayload =
      nextScheduleItems?.nextScheduleMaster ??
      nextScheduleItems?.scheduleMaster;

    if (nextSchedulePayload) {
      dispatch({
        type: DonorSchedulingActions.UPDATE_SCHEDULE_MASTER,
        payload: {
          scheduleMaster: nextSchedulePayload,
          scheduleMasterId: nextSchedulePayload.id,
        },
      });
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Schedule Found',
          detail: `Found matching schedule for ${donor.name}`,
          life: ToastLife.DEFAULT,
        }),
      );
    } else {
      dispatch({
        type: DonorSchedulingActions.UPDATE_SCHEDULE_MASTER,
        payload: {
          scheduleMaster: undefined,
          scheduleMasterId: undefined,
        },
      });
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'No Schedule Found',
          detail: `Found no matching schedule for ${donor.name}`,
          life: ToastLife.DEFAULT,
        }),
      );
    }
    const scheduledDrugs = await getScheduledDrugs(donor.id).unwrap();
    const scheduledDrugsItems = scheduledDrugs.superOvulationSchedules.items[0];
    const drug1 = scheduledDrugsItems?.drug;
    const drug2 = scheduledDrugsItems?.drug2;
    if (isDsDefaultSoEnabled) {
      const hasScheduleMaster = !!nextSchedulePayload;
      const isNoStim = isNoStimSchedule(nextSchedulePayload?.name);

      if (!hasScheduleMaster || isNoStim) {
        // No schedule master or NO STIM — clear both drugs.
        dispatch({
          type: DonorSchedulingActions.UPDATE_FSH_DRUG_1,
          payload: { fshId: undefined, drug: null },
        });
        dispatch(
          addToast({
            severity: 'warn',
            summary: 'No FSH Drug 1 Found',
            detail: `Found no matching FSH Drug 1 for ${donor.name}`,
            life: ToastLife.DEFAULT,
          }),
        );
        dispatch({
          type: DonorSchedulingActions.UPDATE_FSH_DRUG_2,
          payload: { fsh2Id: undefined, drug2: null },
        });
        dispatch(
          addToast({
            severity: 'warn',
            summary: 'No FSH Drug 2 Found',
            detail: `Found no matching FSH Drug 2 for ${donor.name}`,
            life: ToastLife.DEFAULT,
          }),
        );
      } else {
        const resolveFshDrugByName = async (name: string) => {
          const fromLookup = findExactDrugByName(fshDrugs, name);
          if (fromLookup) {
            return fromLookup;
          }
          const results = await searchDrugs({
            search: name,
            selectionLimit: SELECTION_LIMIT,
          }).unwrap();
          return findExactDrugByName(results, name);
        };

        const drug1Payload = await resolveFshDrug1({
          savedFshId: workingDonorSchedule?.fshId,
          savedDrug: workingDonorSchedule?.drug,
          twiceConcentration: nextSchedulePayload?.twiceConcentration,
          resolveDrugByName: resolveFshDrugByName,
        });
        dispatch({
          type: DonorSchedulingActions.UPDATE_FSH_DRUG_1,
          payload: drug1Payload,
        });
        if (drug1Payload.source === 'saved') {
          dispatch(
            addToast({
              severity: 'success',
              summary: 'FSH Drug 1 Found',
              detail: `Found matching FSH Drug 1 for ${donor.name}`,
              life: ToastLife.DEFAULT,
            }),
          );
        } else {
          const defaultName = getFolltropinDrugName(
            nextSchedulePayload?.twiceConcentration,
          );
          dispatch(
            addToast({
              severity: 'warn',
              summary: 'No FSH Drug 1 Found',
              detail: `FSH Drug 1 not found for ${donor.name}, adding ${defaultName} as default`,
              life: ToastLife.DEFAULT,
            }),
          );
        }

        const drug2Payload = resolveFshDrug2({
          savedFsh2Id: workingDonorSchedule?.fsh2Id,
          savedDrug2: workingDonorSchedule?.drug2,
          soDrug2: drug2,
        });
        dispatch({
          type: DonorSchedulingActions.UPDATE_FSH_DRUG_2,
          payload: drug2Payload,
        });
        if (drug2Payload.source !== 'none') {
          dispatch(
            addToast({
              severity: 'success',
              summary: 'FSH Drug 2 Found',
              detail: `Found matching FSH Drug 2 for ${donor.name}`,
              life: ToastLife.DEFAULT,
            }),
          );
        } else {
          dispatch(
            addToast({
              severity: 'warn',
              summary: 'No FSH Drug 2 Found',
              detail: `Found no matching FSH Drug 2 for ${donor.name}`,
              life: ToastLife.DEFAULT,
            }),
          );
        }
      }
    } else {
      if (drug1) {
        dispatch({
          type: DonorSchedulingActions.UPDATE_FSH_DRUG_1,
          payload: {
            fshId: drug1?.id,
            drug: drug1?.name,
          },
        });
        dispatch(
          addToast({
            severity: 'success',
            summary: 'FSH Drug 1 Found',
            detail: `Found matching FSH Drug 1 for ${donor.name}`,
            life: ToastLife.DEFAULT,
          }),
        );
      } else {
        dispatch({
          type: DonorSchedulingActions.UPDATE_FSH_DRUG_1,
          payload: {
            fshId: undefined,
            drug: undefined,
          },
        });
        dispatch(
          addToast({
            severity: 'warn',
            summary: 'No FSH Drug 1 Found',
            detail: `Found no matching FSH Drug 1 for ${donor.name}`,
            life: ToastLife.DEFAULT,
          }),
        );
      }
      if (drug2) {
        dispatch({
          type: DonorSchedulingActions.UPDATE_FSH_DRUG_2,
          payload: {
            fshId: drug2.id,
            drug: drug2.name,
          },
        });
        dispatch(
          addToast({
            severity: 'success',
            summary: 'FSH Drug 2 Found',
            detail: `Found matching FSH Drug 2 for ${donor.name}`,
            life: ToastLife.DEFAULT,
          }),
        );
      } else {
        dispatch({
          type: DonorSchedulingActions.UPDATE_FSH_DRUG_2,
          payload: {
            fshId: undefined,
            drug: null,
          },
        });
        dispatch(
          addToast({
            severity: 'warn',
            summary: 'No FSH Drug 2 Found',
            detail: `Found no matching FSH Drug 2 for ${donor.name}`,
            life: ToastLife.DEFAULT,
          }),
        );
      }
    }
  };

  useEffect(() => {
    if (!isDsDefaultSoEnabled || !workingDonorSchedule?.donorId) return;
    if (donorChangeToastsRef.current === workingDonorSchedule.donorId) return;
    donorChangeToastsRef.current = workingDonorSchedule.donorId;

    const donorName = workingDonorSchedule.donor?.name;
    const scheduleMaster = workingDonorSchedule.scheduleMaster;
    const hasScheduleMaster = !!scheduleMaster;
    const isNoStim = isNoStimSchedule(scheduleMaster?.name);

    if (!hasScheduleMaster || isNoStim) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'No FSH Drug 1 Found',
          detail: `Found no matching FSH Drug 1 for ${donorName}`,
          life: ToastLife.DEFAULT,
        }),
      );
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'No FSH Drug 2 Found',
          detail: `Found no matching FSH Drug 2 for ${donorName}`,
          life: ToastLife.DEFAULT,
        }),
      );
    } else {
      if (workingDonorSchedule.fshId) {
        dispatch(
          addToast({
            severity: 'success',
            summary: 'FSH Drug 1 Found',
            detail: `Found matching FSH Drug 1 for ${donorName}`,
            life: ToastLife.DEFAULT,
          }),
        );
      } else {
        const defaultName = getFolltropinDrugName(
          scheduleMaster?.twiceConcentration,
        );
        dispatch(
          addToast({
            severity: 'warn',
            summary: 'No FSH Drug 1 Found',
            detail: `FSH Drug 1 not found for ${donorName}, adding ${defaultName} as default`,
            life: ToastLife.DEFAULT,
          }),
        );
      }

      if (workingDonorSchedule.fsh2Id) {
        dispatch(
          addToast({
            severity: 'success',
            summary: 'FSH Drug 2 Found',
            detail: `Found matching FSH Drug 2 for ${donorName}`,
            life: ToastLife.DEFAULT,
          }),
        );
      } else {
        dispatch(
          addToast({
            severity: 'warn',
            summary: 'No FSH Drug 2 Found',
            detail: `Found no matching FSH Drug 2 for ${donorName}`,
            life: ToastLife.DEFAULT,
          }),
        );
      }
    }
  }, [isDsDefaultSoEnabled, workingDonorSchedule?.donorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSoScheduleDialogNo = () => {
    setUnlockAll(false);
  };

  const handleBillPartnershipChange = async (e: DropdownChangeEvent) => {
    dispatch({
      type: DonorSchedulingActions.UPDATE_BILL_DONOR_PARTNERSHIP_STATUS,
      payload: e.value,
    });

    const updatedSchedule = removeNulls(
      {
        ...workingDonorSchedule,
        loadDonorPartners: e.value,
      },
      allowedNullFields,
    );
    try {
      const isExistingDonorSchedule =
        id !== null && id !== undefined && id !== '-1';
      if (isExistingDonorSchedule) {
        await upsertDonorSchedules({ input: updatedSchedule });
      }
    } catch (error) {
      console.error(
        'Failed to save donor schedule after bill partnership change:',
        error,
      );
    }
  };

  const getTooltipMessage = () => {
    if (viewOnly) return 'This field is read-only';
    if (isDateLessThanOrEqualToToday) {
      return 'Cannot modify bill partnership where aspiration date is today or in the past';
    }
    if (isCommercialAccount) {
      return 'Cannot modify bill partnership for commercial accounts';
    }
    return undefined;
  };

  const getBillAccountCaningValue = () => {
    if (
      workingDonorSchedule?.loadDonorPartners ===
        LOAD_DONOR_PARTNERS_OPTION_VALUES.YES &&
      workingDonorSchedule?.billAccountCaningOption !== null
    ) {
      return null;
    }
    return workingDonorSchedule?.billAccountCaningOption;
  };

  const getCollapsedValue = () => {
    return workingDonorSchedule?.loadDonorPartners !==
      LOAD_DONOR_PARTNERS_OPTION_VALUES.YES
      ? true
      : collapsed;
  };

  const handleAspirationSiteChange = (site: Selection) => {
    if (!site) return;

    dispatch({
      type: DonorSchedulingActions.UPDATE_ASPIRATION_SITE,
      payload: {
        aspirationSite: site,
        aspirationSiteId: site.id,
      } as DonorSchedule,
    });

    dispatch({
      type: DonorSchedulingActions.UPDATE_FERTILIZATION_SITE,
      payload: {
        fertilizationSite:
          'fertilizationSite' in site ? site.fertilizationSite : undefined,
        fertilizationSiteId:
          'fertilizationSiteId' in site ? site.fertilizationSiteId : undefined,
      } as DonorSchedule,
    });
  };

  const handleDateChange = (e: any) => {
    if (!e.value) return;
    const dateValue = e.value instanceof Date ? e.value : new Date(e.value);
    const dateString = dateValue.toISOString().slice(0, 19);
    dispatch({
      type: DonorSchedulingActions.UPDATE_DATE,
      payload: dateString,
    });
  };

  const validSites = useMemo(() => getValidSites(me), [me]);

  const renderLockIcon = () => {
    if (!hasSoSchedule) return null;

    return (
      <Button
        icon={`pi pi-${unlockAll ? 'lock-open' : 'lock'}`}
        className="p-button-text p-button-sm ml-2"
        onClick={handleSoScheduleWarning}
        tooltip="Click to unlock fields (SO Schedule exists)"
      />
    );
  };

  return (
    <div
      className={`donor-schedule-form-general ${className}`.trim()}
      onKeyDownCapture={(e) => {
        if (e.key === 'Enter') {
          handleEnterKeyAdvance(e);
        } else {
          onKeyDownCapture(e);
        }
      }}
      ref={formNavRef}
    >
      <Panel
        className="column-span"
        data-pr-disabled={!workingDonorSchedule?.donor}
        header={`${t('donorScheduling.partnership')} - ${
          workingDonorSchedule?.donor
            ? workingDonorSchedule?.donor?.name
            : t('donor.selectDonor')
        }`}
        toggleable
        collapsed={getCollapsedValue()}
        onToggle={(e: PanelToggleEvent) => onCollapseChange(e.value)}
      >
        <PartnershipForm.Root>
          <PartnershipForm.Tabs
            types={[
              {
                attributeValue: PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING,
                value:
                  PartnershipTypeLabel[
                    PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING
                  ],
              },
              {
                attributeValue: PartnershipTypeValue.SPECIAL_BILLING,
                value:
                  PartnershipTypeLabel[PartnershipTypeValue.SPECIAL_BILLING],
              },
            ]}
            viewOnly={viewOnly}
          />
        </PartnershipForm.Root>
      </Panel>

      <div className="grid w-full grid-cols-2 gap-6">
        <div className="label-input-group">
          <label>
            {t('donorScheduling.opuSite')}
            {isRequiredFieldAsterisksEnabled && '*'}
          </label>
          <div className="flex w-full flex-row">
            <SitesDropdown
              className="w-full"
              value={workingDonorSchedule?.aspirationSite ?? null}
              disabled={viewOnly || isFieldLocked}
              field="siteName"
              onChange={handleAspirationSiteChange}
              showAllLocations={true}
              validSites={validSites}
              alwaysSyncSelection={isDsDefaultSoEnabled}
            />
            {!isPastDue && renderLockIcon()}
          </div>
        </div>

        <div className="label-input-group">
          {isAdvancedAccountSearchEnabled ? (
            <AdvancedAccountLookup
              container={dialogContainer}
              label={t('donorScheduling.textMessageAccount')}
              value={workingDonorSchedule?.textingAccount ?? null}
              onChange={(account) => {
                dispatch({
                  type: DonorSchedulingActions.UPDATE_TEXTING_ACCOUNT,
                  payload: account,
                });
              }}
              disabled={viewOnly}
              tabIndex={2}
            />
          ) : (
            <AccountAutoComplete
              label={t('donorScheduling.textMessageAccount')}
              defaultValue={workingDonorSchedule?.textingAccount ?? null}
              field="name"
              disabled={viewOnly}
              onSelect={(e: AutoCompleteSelectEvent) =>
                dispatch({
                  type: DonorSchedulingActions.UPDATE_TEXTING_ACCOUNT,
                  payload: e.value,
                })
              }
              tabIndex={2}
            />
          )}
        </div>

        <div className="flex w-full flex-row gap-4">
          <div className="label-input-group">
            <label>{t('common.date')}*</label>
            <Calendar
              container={dialogContainer}
              value={
                workingDonorSchedule?.date
                  ? new Date(workingDonorSchedule.date)
                  : null
              }
              minDate={minDate}
              showIcon
              showOnFocus={false}
              disabled={viewOnly || isFieldLocked}
              onChange={handleDateChange}
              className="w-full"
              tabIndex={3}
            />
          </div>
          <div className="label-input-group w-1/4">
            <label>{t('common.dow')}</label>
            <InputText
              value={workingDonorSchedule?.dow ?? ''}
              disabled={true}
              readOnly={true}
              className="w-full"
              tabIndex={4}
            />
          </div>
          <div className="label-input-group">
            <label></label>
            {!isPastDue && renderLockIcon()}
          </div>
        </div>

        <div className="label-input-group">
          <label>{t('donorScheduling.textMessageNumber')}</label>
          <InputMask
            value={workingDonorSchedule?.textingNumber ?? null}
            disabled={viewOnly}
            onChange={(e: InputMaskChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_TEXTING_NUMBER,
                payload: e.target.value,
              })
            }
            mask="(999) 999-9999"
            placeholder="(999) 999-9999"
            tabIndex={5}
          />
        </div>

        <div className="label-input-group">
          <div className="flex w-full flex-row">
            {isAdvancedAccountSearchEnabled ? (
              <AdvancedAccountLookup
                container={dialogContainer}
                className="w-full"
                label={t('common.account')}
                value={workingDonorSchedule?.owner ?? null}
                onChange={(account) => {
                  dispatch({
                    type: DonorSchedulingActions.UPDATE_OWNER,
                    payload: account,
                  });
                }}
                disabled={viewOnly || isFieldLocked}
                tabIndex={6}
              />
            ) : (
              <AccountAutoComplete
                className="w-full"
                label={t('common.account')}
                defaultValue={workingDonorSchedule?.owner ?? null}
                field="name"
                disabled={viewOnly || isFieldLocked}
                onSelect={(e: AutoCompleteSelectEvent) => {
                  dispatch({
                    type: DonorSchedulingActions.UPDATE_OWNER,
                    payload: e.value,
                  });
                }}
                tabIndex={6}
              />
            )}
            <div className="label-input-group">
              <label> </label>
              {!isPastDue && renderLockIcon()}
            </div>
          </div>
        </div>

        <div className="label-input-group">
          <label>{t('donorScheduling.aspirationComment')}</label>
          <InputText
            value={workingDonorSchedule?.specialInstructions ?? ''}
            disabled={viewOnly}
            onInput={(e: React.ChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_SPECIAL_INSTRUCTIONS,
                payload: e.target.value,
              })
            }
            tabIndex={7}
          />
        </div>

        <div className="label-input-group">
          <div className="flex w-full flex-row">
            <AdvancedDonorLookup
              className="general-donor-lookup"
              label={t('donor.donor')}
              value={
                workingDonorSchedule?.donor?.id
                  ? workingDonorSchedule?.donor
                  : null
              }
              ownerId={workingDonorSchedule?.owner?.id}
              onChange={handleDonorChange}
              revisionComment={revisionComment}
              setRevisionComment={setRevisionComment}
              revisionNumber={revisionNumber}
              setRevisionNumber={setRevisionNumber}
              disabled={viewOnly || isFieldLocked}
              tabIndex={8}
            />
            <div className="label-input-group">
              <label></label>
              {!isPastDue && renderLockIcon()}
            </div>
          </div>
        </div>
        <div className="label-input-group">
          <label>{t('fields.registrationNumber')}</label>
          <InputText
            value={workingDonorSchedule?.donor?.registrationNumber ?? ''}
            disabled
            readOnly
            className="p-inputtext-disabled"
          />
        </div>
        <div className="label-input-group">
          <CsrAutoComplete
            label={t('common.csr') + '*'}
            defaultValue={csr}
            field="fullName"
            disabled={viewOnly}
            onSelect={(e: AutoCompleteSelectEvent) => {
              dispatch({
                type: DonorSchedulingActions.UPDATE_CSR,
                payload: {
                  fullName: e.value.fullName,
                  id: e.value.id,
                },
              });
            }}
            tabIndex={9}
          />
        </div>

        <div className="label-input-group">
          <label>{t('donor.donorLocation')}</label>
          <InputText
            value={workingDonorSchedule?.donorLocation ?? ''}
            disabled={viewOnly}
            onInput={(e: React.ChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_DONOR_LOCATION,
                payload: e.target.value,
              })
            }
            tabIndex={10}
          />
        </div>
        <div className="label-input-group-wrapper flex items-end gap-4">
          <div className="label-input-group flex-1">
            {workingDonorSchedule?.loadDonorPartners ===
            LOAD_DONOR_PARTNERS_OPTION_VALUES.NO ? (
              <AccountAutoComplete
                label={t('common.billingAccount') + '*'}
                defaultValue={workingDonorSchedule?.billingAccount ?? null}
                field="name"
                disabled={viewOnly || isDateLessThanOrEqualToToday}
                onSelect={(e: AutoCompleteSelectEvent) => {
                  dispatch({
                    type: DonorSchedulingActions.UPDATE_BILLING_ACCOUNT,
                    payload: e.value,
                  });
                }}
                tabIndex={11}
              />
            ) : (
              <div className="label-input-group">
                <label>{t('common.billingAccount')}</label>
                <InputText
                  value={'See partnership info above'}
                  disabled={true}
                  readOnly={true}
                  tabIndex={11}
                />
              </div>
            )}
          </div>
          <div className="label-input-group flex-shrink-0">
            <label>{t('donorScheduling.billPartnership') + '*'}</label>
            <SelectButton
              className={`p-nucleus ${
                viewOnly || isDateLessThanOrEqualToToday || isCommercialAccount
                  ? 'cursor-not-allowed opacity-50'
                  : ''
              }`}
              value={workingDonorSchedule?.loadDonorPartners ?? null}
              allowEmpty={true}
              disabled={false}
              onChange={
                viewOnly || isDateLessThanOrEqualToToday || isCommercialAccount
                  ? () => {}
                  : handleBillPartnershipChange
              }
              tabIndex={12}
              options={BILL_PARTNERSHIP_SELECT_OPTIONS}
              optionLabel="name"
              optionValue="value"
              tooltip={getTooltipMessage()}
              tooltipOptions={{ position: 'top', className: 'max-w-xs' }}
            />
          </div>
        </div>

        <div className="label-input-group">
          <StringMapDropdown
            appendTo={undefined}
            label={t('donorScheduling.procedureTime')}
            value={workingDonorSchedule?.procedureTime ?? ''}
            objectTypeCode={StringMapObjectTypeCode.PROCEDURE_TIME}
            attributeName="tog_procedureTime"
            disabled={viewOnly}
            onChange={(e: DropdownChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_PROCEDURE_TIME,
                payload: e.value,
              })
            }
            tabIndex={13}
          />
        </div>

        <div className="label-input-group">
          <label>{t('donorScheduling.billAccountCaningOption')}</label>
          <Dropdown
            appendTo={undefined}
            value={getBillAccountCaningValue()}
            disabled={
              viewOnly ||
              workingDonorSchedule?.loadDonorPartners !==
                LOAD_DONOR_PARTNERS_OPTION_VALUES.NO
            }
            onChange={(e: DropdownChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_BILL_ACCOUNT_CANING_OPTIONS,
                payload: e.value,
              })
            }
            options={BILL_ACCOUNT_CANING_OPTIONS}
            optionLabel="name"
            optionValue="value"
            tabIndex={14}
          />
        </div>

        <div className="label-input-group">
          <label>{t('donorScheduling.fertilizationSite')}*</label>
          <FertilizationSiteDropdown
            appendTo={null}
            value={workingDonorSchedule?.fertilizationSiteId ?? ''}
            disabled={viewOnly}
            onChange={(e: DropdownChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_FERTILIZATION_SITE,
                payload: {
                  fertilizationSiteId: e.value,
                  //fertilizationSiteId: e.value?.id,
                } as DonorSchedule,
              })
            }
            tabIndex={15}
          />
        </div>
        <div className="label-input-group">
          <label>{t('donor.donorComment')}</label>
          <InputText
            value={workingDonorSchedule?.donorComment ?? ''}
            disabled={viewOnly}
            onInput={(e: React.ChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_DONOR_COMMENT,
                payload: e.target.value,
              })
            }
            tabIndex={16}
          />
        </div>
        <div className="label-input-group">
          <label>{t('donorScheduling.matingPriority')}</label>
          <InputNumber
            value={workingDonorSchedule?.priority ?? null}
            disabled={viewOnly}
            onChange={(e: InputNumberChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_PRIORITY,
                payload: e.value ?? null,
              })
            }
            tabIndex={17}
          />
        </div>
        <div className="label-input-group">
          <label>{t('donorScheduling.programName')}</label>
          <InputText
            value={workingDonorSchedule?.programName ?? ''}
            disabled={viewOnly}
            onInput={(e: React.ChangeEvent) => {
              dispatch({
                type: DonorSchedulingActions.UPDATE_PROGRAM_NAME,
                payload: e.target.value,
              });
            }}
            tabIndex={18}
          />
        </div>
        <div></div>
        <div className="flex items-end gap-4">
          <div className="label-input-group">
            <label>{t('donor.pregnant')}</label>
            <SelectButton
              className="p-nucleus"
              value={
                workingDonorSchedule?.pregnant === PREGNANT_OPTION_VALUES.YES
                  ? 1
                  : workingDonorSchedule?.pregnant === PREGNANT_OPTION_VALUES.NO
                    ? 0
                    : null
              }
              disabled={viewOnly}
              onChange={(e: DropdownChangeEvent) =>
                dispatch({
                  type: DonorSchedulingActions.UPDATE_PREGNANT,
                  payload: e.value
                    ? PREGNANT_OPTION_VALUES.YES
                    : PREGNANT_OPTION_VALUES.NO,
                })
              }
              options={ZERO_ONE_SELECT_OPTIONS}
              optionLabel="name"
              optionValue="value"
              tabIndex={19}
            />
          </div>

          <div className="label-input-group">
            <label>{t('donorScheduling.pool')}</label>
            <SelectButton
              className="p-nucleus"
              value={workingDonorSchedule?.pool ?? null}
              allowEmpty={false}
              disabled={viewOnly}
              onChange={(e: DropdownChangeEvent) =>
                dispatch({
                  type: DonorSchedulingActions.UPDATE_POOL,
                  payload: e.value,
                })
              }
              options={TRUE_FALSE_SELECT_OPTIONS}
              optionLabel="name"
              optionValue="value"
              tabIndex={20}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
